/**
 * EffectForm.js — Type-specific form for editing an effect
 *
 * Renders fields appropriate to each of the 25 effect types,
 * grouped by category: State, Resources, Combat, Social, UI.
 */

import { useId } from 'react';
import { cn } from '../../../../lib/utils';

const inputClass = cn(
  'bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white text-sm',
  'focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none w-full'
);

const labelClass = 'text-sm font-medium text-gray-300 mb-1 block';

// Grouped effect types for the dropdown
const EFFECT_GROUPS = [
  {
    label: 'State',
    types: ['set_session_state', 'clear_session_state', 'set_character_flag', 'clear_character_flag', 'toggle_character_flag'],
  },
  {
    label: 'Resources',
    types: ['gain_hope', 'clear_stress', 'mark_stress', 'heal_hp', 'gain_token', 'reset_token', 'initialize_token'],
  },
  {
    label: 'Combat',
    types: ['grant_armor', 'set_advantage', 'add_bonus_dice', 'trigger_spell_attack', 'trigger_trait_roll', 'trigger_weapon_roll'],
  },
  {
    label: 'Social',
    types: ['heal_ally', 'clear_ally_stress', 'grant_ally_hope', 'grant_ally_armor'],
  },
  {
    label: 'UI',
    types: ['show_toast', 'switch_tab', 'roll_local_dice'],
  },
];

const EffectForm = ({ effect, onUpdate }) => {
  const baseId = useId();
  const handleTypeChange = (newType) => {
    const base = { type: newType };
    switch (newType) {
      case 'set_session_state': onUpdate({ ...base, stateKey: '', value: true }); break;
      case 'clear_session_state': onUpdate({ ...base, stateKey: '' }); break;
      case 'set_character_flag':
      case 'clear_character_flag':
      case 'toggle_character_flag': onUpdate({ ...base, flag: '' }); break;
      case 'gain_hope':
      case 'clear_stress':
      case 'mark_stress':
      case 'heal_hp': onUpdate({ ...base, amount: 1 }); break;
      case 'gain_token': onUpdate({ ...base, tokenId: '', amount: 1 }); break;
      case 'reset_token':
      case 'initialize_token': onUpdate({ ...base, tokenId: '' }); break;
      case 'grant_armor':
      case 'add_bonus_dice': onUpdate({ ...base, amount: 1 }); break;
      case 'heal_ally':
      case 'clear_ally_stress':
      case 'grant_ally_hope':
      case 'grant_ally_armor': onUpdate({ ...base, amount: 1 }); break;
      case 'show_toast': onUpdate({ ...base, message: '' }); break;
      case 'switch_tab': onUpdate({ ...base, tab: '' }); break;
      default: onUpdate(base);
    }
  };

  const renderFields = () => {
    switch (effect.type) {
      // State
      case 'set_session_state':
        return (
          <div className="space-y-2">
            <div>
              <label htmlFor={`${baseId}-state-key`} className={labelClass}>State Key</label>
              <input
                id={`${baseId}-state-key`}
                type="text"
                value={effect.stateKey || ''}
                onChange={(e) => onUpdate({ ...effect, stateKey: e.target.value })}
                placeholder="e.g. absorb-active"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor={`${baseId}-value`} className={labelClass}>Value</label>
              <input
                id={`${baseId}-value`}
                type="text"
                value={typeof effect.value === 'boolean' ? String(effect.value) : (effect.value || '')}
                onChange={(e) => {
                  const v = e.target.value;
                  const parsed = v === 'true' ? true : v === 'false' ? false : v;
                  onUpdate({ ...effect, value: parsed });
                }}
                placeholder="true / false / custom"
                className={inputClass}
              />
            </div>
          </div>
        );
      case 'clear_session_state':
        return (
          <div>
            <label htmlFor={`${baseId}-state-key`} className={labelClass}>State Key</label>
            <input
              id={`${baseId}-state-key`}
              type="text"
              value={effect.stateKey || ''}
              onChange={(e) => onUpdate({ ...effect, stateKey: e.target.value })}
              placeholder="e.g. absorb-active"
              className={inputClass}
            />
          </div>
        );
      case 'set_character_flag':
      case 'clear_character_flag':
      case 'toggle_character_flag':
        return (
          <div>
            <label htmlFor={`${baseId}-flag`} className={labelClass}>Flag Name</label>
            <input
              id={`${baseId}-flag`}
              type="text"
              value={effect.flag || ''}
              onChange={(e) => onUpdate({ ...effect, flag: e.target.value })}
              placeholder="e.g. beast-form-active"
              className={inputClass}
            />
          </div>
        );

      // Resources
      case 'gain_hope':
      case 'clear_stress':
      case 'mark_stress':
      case 'heal_hp':
        return (
          <div>
            <label htmlFor={`${baseId}-amount`} className={labelClass}>Amount</label>
            <input
              id={`${baseId}-amount`}
              type="number"
              min={1}
              value={effect.amount ?? 1}
              onChange={(e) => onUpdate({ ...effect, amount: Number(e.target.value) })}
              className={inputClass}
            />
          </div>
        );
      case 'gain_token':
        return (
          <div className="space-y-2">
            <div>
              <label htmlFor={`${baseId}-token-id`} className={labelClass}>Token ID</label>
              <input
                id={`${baseId}-token-id`}
                type="text"
                value={effect.tokenId || ''}
                onChange={(e) => onUpdate({ ...effect, tokenId: e.target.value })}
                placeholder="e.g. rage-tokens"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor={`${baseId}-token-amount`} className={labelClass}>Amount</label>
              <input
                id={`${baseId}-token-amount`}
                type="number"
                min={1}
                value={effect.amount ?? 1}
                onChange={(e) => onUpdate({ ...effect, amount: Number(e.target.value) })}
                className={inputClass}
              />
            </div>
          </div>
        );
      case 'reset_token':
      case 'initialize_token':
        return (
          <div>
            <label htmlFor={`${baseId}-token-id`} className={labelClass}>Token ID</label>
            <input
              id={`${baseId}-token-id`}
              type="text"
              value={effect.tokenId || ''}
              onChange={(e) => onUpdate({ ...effect, tokenId: e.target.value })}
              placeholder="e.g. rage-tokens"
              className={inputClass}
            />
          </div>
        );

      // Combat
      case 'grant_armor':
      case 'add_bonus_dice':
        return (
          <div>
            <label htmlFor={`${baseId}-combat-amount`} className={labelClass}>Amount</label>
            <input
              id={`${baseId}-combat-amount`}
              type="number"
              min={1}
              value={effect.amount ?? 1}
              onChange={(e) => onUpdate({ ...effect, amount: Number(e.target.value) })}
              className={inputClass}
            />
          </div>
        );
      case 'set_advantage':
      case 'trigger_spell_attack':
      case 'trigger_trait_roll':
      case 'trigger_weapon_roll':
      case 'roll_local_dice':
        return (
          <div className="text-sm text-gray-400 italic">
            No additional configuration needed.
          </div>
        );

      // Social
      case 'heal_ally':
      case 'clear_ally_stress':
      case 'grant_ally_hope':
      case 'grant_ally_armor':
        return (
          <div>
            <label htmlFor={`${baseId}-social-amount`} className={labelClass}>Amount</label>
            <input
              id={`${baseId}-social-amount`}
              type="number"
              min={1}
              value={effect.amount ?? 1}
              onChange={(e) => onUpdate({ ...effect, amount: Number(e.target.value) })}
              className={inputClass}
            />
          </div>
        );

      // UI
      case 'show_toast':
        return (
          <div className="space-y-2">
            <div>
              <label htmlFor={`${baseId}-message`} className={labelClass}>Message</label>
              <input
                id={`${baseId}-message`}
                type="text"
                value={effect.message || ''}
                onChange={(e) => onUpdate({ ...effect, message: e.target.value })}
                placeholder="Ability activated!"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor={`${baseId}-icon`} className={labelClass}>Icon (optional)</label>
              <input
                id={`${baseId}-icon`}
                type="text"
                value={effect.icon || ''}
                onChange={(e) => onUpdate({ ...effect, icon: e.target.value })}
                placeholder="e.g. shield"
                className={inputClass}
              />
            </div>
          </div>
        );
      case 'switch_tab':
        return (
          <div>
            <label htmlFor={`${baseId}-tab`} className={labelClass}>Tab Name</label>
            <input
              id={`${baseId}-tab`}
              type="text"
              value={effect.tab || ''}
              onChange={(e) => onUpdate({ ...effect, tab: e.target.value })}
              placeholder="e.g. abilities"
              className={inputClass}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor={`${baseId}-type`} className={labelClass}>Effect Type</label>
        <select
          id={`${baseId}-type`}
          value={effect.type || ''}
          onChange={(e) => handleTypeChange(e.target.value)}
          className={inputClass}
        >
          {EFFECT_GROUPS.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.types.map((t) => (
                <option key={t} value={t}>
                  {t.replace(/_/g, ' ')}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      {renderFields()}
    </div>
  );
};

export default EffectForm;
